import React, { useState } from "react";
import CustomTable from "components/common/CustomTable";
import { Tooltip } from "react-tooltip";
import { truncate } from "lodash";
import { useNavigate } from "react-router-dom";
import millify from "millify";
import clsx from "clsx";
import moment from "moment";

import VendorAvatar from "components/common/VendorAvatar";
import { formatMoney } from "helpers";
import EmptyStateComp from "components/common/EmptyStateComp";
import Button from "components/common/Button";
import SearchTextField from "components/common/InputField/SearchTextField";
import Paper from "components/common/Paper";

type InventoryComponentSummaryProps = {
    data: Record<string, any>[];
    open: boolean;
    closeModal: () => void;
};

const InventoryComponentSummary = (props: InventoryComponentSummaryProps) => {
    const { data, closeModal, open, ...rest } = props;

    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState("");

    const tableHeader = [
        {
            title: "Component",
            widthClass: "!border-slate-200 w-[180px]",
            tooltipIcon: "component",
            tooltipContent:
                "These are products that typically make up the production of other products (finished goods). They are also known as recipes or ingredients and can be tracked individually.",
        },
        {
            title: "Current stock level",
            widthClass: "!border-slate-200 w-[150px]",
            tooltipIcon: "current-stock-level-2",
            tooltipContent:
                "The actual quantity of the product currently available in stock for sale or use at this moment.",
        },
        {
            title: "Forecasted qty to be used",
            widthClass: "!border-slate-200 w-[200px]",
            tooltipIcon: "forecasted-qty-2",
            tooltipContent:
                "The projected quantity of this component expected to be used across all SKUs it's part of—not just a single finished good based on future demand trends and planned production activities.",
        },
        {
            title: "Forecasted needs",
            widthClass: "!border-slate-200 w-[200px]",
            tooltipIcon: "forecasted-needs",
            tooltipContent:
                "The estimated amount of the product required to meet anticipated demand over your selected forecast window (between 1 and 6 months)",
        },
        {
            title: "Current lead time",
            widthClass: "!border-slate-200 w-[130px]",
            tooltipIcon: "current-lead-time",
            tooltipContent:
                "The average time it takes for a supplier to deliver the product after an order is placed, typically measured in days.",
        },
        {
            title: "Forecasted cost",
            widthClass: "!border-slate-200 w-[172px]",
            tooltipIcon: "forecasted-cost",
            tooltipContent:
                "The predicted total cost of acquiring the forecasted quantity needed, factoring in product unit current price and forecasted demanded quantity.",
        },
        {
            title: "Re-order before",
            widthClass: "!border-slate-200 w-[158px]",
            tooltipIcon: "re-order-before",
            tooltipContent:
                "The latest date you should place a purchase order to avoid stockouts, based on lead time and forecasted needs. If the required date is within 7 days, we show “Immediately” & “the today's date” to signal urgency.",
        },
    ];

    const tableBody = data
        ?.filter((comp) => comp?.productName?.toLowerCase().includes(searchValue?.toLowerCase()))
        ?.map((comp, idx) => {
            const givenDate = moment(comp?.forecastData?.stockoutDate);
            const today = moment().startOf("day");
            let differenceInDays = 0;

            if (givenDate.isValid()) {
                differenceInDays = givenDate.diff(today, "days");
            }
            return [
                {
                    content: (
                        <div
                            className={`py-2 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                        >
                            <div className="flex items-center space-x-3">
                                {comp?.productImageDetails?.productImage?.[0] ? (
                                    <VendorAvatar
                                        imageSrc={comp?.productImageDetails?.productImage?.[0]}
                                        size={32}
                                        name={comp?.productName}
                                        containerClassname="!rounded-md"
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                        <i className="ri-box-2-fill text-[20px] text-slate-500"></i>
                                    </div>
                                )}
                                <div>
                                    <div>
                                        <a id={`product-column-${idx}`}>
                                            <span className="block text-sm">
                                                {truncate(comp?.productName, {
                                                    length: 20,
                                                })}
                                            </span>
                                        </a>
                                        <Tooltip
                                            anchorSelect={`#product-column-${idx}`}
                                            place={"right"}
                                            content={comp?.productName}
                                            style={{
                                                maxWidth: "250px",
                                                zIndex: 9999,
                                                background: "#fff",
                                                borderRadius: "6px",
                                                border: "1px solid #E2E8F0",
                                                color: "#1E293B",
                                                boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ),

                    tableDataDivClass: "!pl-0",
                    widthClass: "!border-slate-200",
                    cellClickAction: () => {
                        navigate(`/dashboard/inventory/products/${comp?._id}`, {
                            state: { internalNavigation: true },
                        });
                    },
                },

                {
                    content: (
                        <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                            <p className="">
                                {millify(comp?.productStockDetails?.stockLevel || 0, {
                                    precision: 2,
                                }) || 0}{" "}
                                {comp?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    ),
                    widthClass: "!border-slate-200",
                },
                {
                    content: (
                        <div
                            className={`py-3 pr-3 font-rocGroteskMedium border-slate-200 text-sm text-g-75 `}
                        >
                            <span className="">
                                {comp?.forecastData?.forecast ? (
                                    <span className="">
                                        {millify(comp?.forecastData?.forecast, { precision: 2 })}{" "}
                                        {comp?.productStockDetails?.unitOfMeasurement || ""}
                                    </span>
                                ) : (
                                    "Not Available"
                                )}
                            </span>
                        </div>
                    ),
                    widthClass: "!border-slate-200",
                },
                {
                    content: (
                        <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                            <p>
                                {comp?.forecastData?.forecastedNeeds ? (
                                    <div className="flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[14px] text-r-50">
                                            warning
                                        </span>
                                        <span className="text-[#FF3D1D]">
                                            {millify(comp?.forecastData?.forecastedNeeds, {
                                                precision: 2,
                                            })}{" "}
                                            {comp?.productStockDetails?.unitOfMeasurement || ""}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-[#56B648]">Sufficient</span>
                                )}
                            </p>
                        </div>
                    ),
                    widthClass: "!border-slate-200",
                },
                {
                    content: (
                        <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                            <p className="">
                                {comp?.leadTime?.value ? (
                                    <span className="">
                                        {comp?.leadTime?.value} {comp?.leadTime?.duration || "days"}
                                    </span>
                                ) : comp?.forecastData?.leadTime ? (
                                    <span className="">{comp?.forecastData?.leadTime} days</span>
                                ) : (
                                    "Not Available"
                                )}
                            </p>
                        </div>
                    ),
                    widthClass: "!border-slate-200",
                },
                {
                    content: (
                        <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                            <p className="">
                                {comp?.forecastData?.forecastedNeeds
                                    ? `${"$ "}${millify(
                                          comp?.forecastData?.forecastedNeeds *
                                              comp?.unitCost?.amount,
                                          { precision: 2 }
                                      )}`
                                    : "Not Available"}
                            </p>
                        </div>
                    ),
                    widthClass: "!border-slate-200",
                },

                {
                    content: (
                        <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                            {!comp?.forecastData?.incidentType &&
                            !comp?.forecastData?.stockoutDate ? (
                                "Not Available"
                            ) : comp?.forecastData?.incidentType?.toLowerCase() !== "stockout" ? (
                                <span className=""> Overstocked </span>
                            ) : // comp?.forecastData?.incidentType?.toLowerCase() === "stockout" ||
                            differenceInDays > 0 && differenceInDays <= 7 ? (
                                <>
                                    <span className="text-[#E4281D] block"> Immediately </span>
                                    <span className={` text-slate-500 block`}>
                                        {moment().format("MMM DD, YYYY")}
                                    </span>
                                </>
                            ) : differenceInDays < 0 ? (
                                <span className="text-[#E4281D]"> Stock out </span>
                            ) : (
                                <>
                                    <span
                                        className={`block ${
                                            differenceInDays > 10
                                                ? "text-carribean-green"
                                                : "text-[#E4281D]"
                                        }`}
                                    >
                                        {differenceInDays === 0
                                            ? "Today"
                                            : formatMoney().format(differenceInDays) + " days"}
                                    </span>
                                    <span className={`block text-slate-500`}>
                                        {comp?.forecastData?.stockoutDate
                                            ? moment(comp?.forecastData?.stockoutDate).format(
                                                  "MMM DD, YYYY"
                                              )
                                            : ""}
                                    </span>
                                </>
                            )}
                        </div>
                    ),
                    widthClass: "!border-slate-200 !border-b ",
                },
            ];
        });

    return (
        <Paper className={clsx("!min-h-[60vh]", open ? "block" : "hidden")}>
            <div className="bg-white flex flex-col rounded-[8px] overflow-x-hidden ">
                <div className="p-4 flex justify-between items-center border-b border-slate-200">
                    <p className={`text-[18px] font-rocGroteskMedium flex gap-2`}>
                        <button onClick={closeModal} className="flex items-center gap-2">
                            <i className="ri-arrow-left-line text-md text-gm-50"></i>{" "}
                        </button>
                        Products components summary
                    </p>
                    <Button
                        btnText="Close"
                        btnClassname="!px-3 !py-1.5  !w-fit !text-[13px] !border-none !rounded-[4px] !text-[#000]"
                        onClick={closeModal}
                    />
                </div>

                <div className="p-4 max-h-[500px] overflow-y-auto">
                    <SearchTextField
                        inputPlaceholder="Search components"
                        onChange={(e) => {
                            console.log(e.target.value);
                            setSearchValue(e.target.value);
                        }}
                        value={searchValue}
                    />
                    <div className="mt-4">
                        <CustomTable
                            tableBody={tableBody}
                            tableHeader={tableHeader}
                            isCellBordered={true}
                            stickyHeader={true}
                            headerContainerClass="!bg-slate-50 !border-slate-200"
                            headerItemClass="font-rocGroteskMedium "
                            tableClass="mb-12 !border-slate-200"
                            isCollapseable={false}
                            // isCollapseableIconIndex={1}
                            allCellClick={false}
                            scrollContentId="table-container-one"
                            renderEmptyTable={() => (
                                <EmptyStateComp
                                    title="No components available"
                                    description={
                                        "Select products don't have any components in the inventory."
                                    }
                                    icon={
                                        <i className="ri-box-3-line text-slate-500 text-[40px]"></i>
                                    }
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </Paper>
    );
};
export default InventoryComponentSummary;
